// src/pages/orders/OrdersPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import Textarea from "../../components/ui/Textarea";
import {
    getOrdersRequest,
    addOrderPaymentRequest,
    updateOrderRequest,
    applyOrderDiscountRequest,
    sendOrderInvoiceRequest,
    sendOrderTicketsRequest,
    sendOrderLieferscheinRequest,
    downloadOrdersGroupedExcelRequest,
    downloadOrderDeliveryDocxRequest,
    sendOrderEmailRequest,
    triggerBrowserDownload,
} from "../../services/api/orders.api";
import { OrderItem, ListOrdersFilters } from "../../services/types/orders.types";
import AddPaymentModal from "../../components/orders/AddPaymentModal";
import ApplyDiscountModal from "../../components/orders/ApplyDiscountModal";
import OrderTicketsModal from "../../components/orders/OrderTicketsModal";
import Modal from "../../components/ui/Modal";
import ConfirmModal from "../../components/ui/ConfirmModal";
import RestoreOrderModal from "../../components/orders/RestoreOrderModal";
import {CreditCard, Send, Undo2, XCircle, FileText, Ticket, Truck, Percent, MessageSquare, MessageSquareText, Mail, RotateCcw} from "lucide-react";
import { getUsersRequest, UserItem } from "../../services/api/users.api";
import { getToursRequest, getTourCitiesRequest } from "../../services/api/event-content.api";
import { TourItem, TourCityItem } from "../../services/types/event-content.types";
import { getAdminEmail } from "../../services/storage/token.storage";

// email админов, которым запрещено вносить оплату из панели управления
// (иконка "Внести оплату" для них скрывается в списке заказов)
const PAYMENT_RESTRICTED_ADMIN_EMAILS = ["office@showmust.de"];
const PAGE_SIZE = 20;

const toMoneyValue = (value?: string | number | null) => {
    if (value === null || value === undefined || value === "") {
        return 0;
    }

    return Number(value);
};

// Партнёрский заказ: чей (Artist Production / Nashe), номер заказа у партнёра и
// оплачен/выкуплен ли он у партнёра (тогда удалить у нас можно только после
// подтверждения, что партнёр удалил заказ у себя).
type PartnerOrderInfo = {
    partnerName: string;
    reference: string; // «заказ № 555» / «резерв № 101»
    paidAtPartner: boolean;
};

const getPartnerOrderInfo = (order: OrderItem): PartnerOrderInfo | null => {
    if (!order.is_partner_order) return null;
    const chunks = order.nashe_data?.chunks ?? [];
    if (order.nashe_data) {
        const orderIds = Array.from(
            new Set(chunks.map((c) => c.nashe_order_id).filter((v) => v !== null && v !== undefined && v !== "")),
        ).map(String);
        const reservationIds = chunks.map((c) => c.reservation_id).filter(Boolean).map(String);
        const purchased = chunks.some((c) => c.status === "purchased");
        return {
            partnerName: "Nashe",
            reference: orderIds.length
                ? `заказ № ${orderIds.join(", ")}`
                : reservationIds.length
                    ? `резерв № ${reservationIds.join(", ")}`
                    : "номер не получен",
            paidAtPartner: Boolean(order.is_paid || order.partner_confirmed_at || purchased),
        };
    }
    return {
        partnerName: "Artist Production",
        // Номера нет: резерв у партнёра не был создан (ошибка при оформлении) или заказ
        // отменили до обновления API, когда номер при отмене ещё стирался.
        reference: order.partner_order_id ? `заказ № ${order.partner_order_id}` : "номер заказа не сохранён",
        paidAtPartner: Boolean(order.is_paid || order.partner_confirmed_at),
    };
};

const OrdersPage: React.FC = () => {
    // если вошёл админ из списка ограниченных — ему нельзя вносить оплату через панель
    const isPaymentRestrictedAdmin = PAYMENT_RESTRICTED_ADMIN_EMAILS.includes(
        (getAdminEmail() || "").toLowerCase()
    );
    const [items, setItems] = useState<OrderItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(0);

    // фильтры (текстовые поля — значение в input обновляется сразу, а запрос к serverу уходит
    // через debounced-версию ниже, чтобы быстрый ввод не слал запросом на каждый символ)
    const [orderNumber, setOrderNumber] = useState("");
    const [customerName, setCustomerName] = useState("");
    // Фильтр по email можно передать ссылкой: /orders?customer_email=... (например, из абонементов).
    const initialCustomerEmail = new URLSearchParams(window.location.search).get("customer_email") || "";
    const [customerEmail, setCustomerEmail] = useState(initialCustomerEmail);
    const [customerPhoneSearch, setCustomerPhoneSearch] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [commentSearch, setCommentSearch] = useState("");
    const [debouncedOrderNumber, setDebouncedOrderNumber] = useState("");
    const [debouncedCustomerName, setDebouncedCustomerName] = useState("");
    const [debouncedCustomerEmail, setDebouncedCustomerEmail] = useState(initialCustomerEmail);
    const [debouncedCustomerPhone, setDebouncedCustomerPhone] = useState("");
    const [debouncedInvoiceNumber, setDebouncedInvoiceNumber] = useState("");
    const [debouncedCommentSearch, setDebouncedCommentSearch] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [isPaidFilter, setIsPaidFilter] = useState<"" | "true" | "false">("");
    const [isDeletedFilter, setIsDeletedFilter] = useState<"" | "true" | "false">("");
    // Доставка билетов почтой: все / есть / нет.
    const [deliveryFilter, setDeliveryFilter] = useState<"" | "true" | "false">("");
    // Способ оплаты: все / R (счёт) / S (Stripe) / C (наличные и наложенный платёж).
    const [paymentTypeFilter, setPaymentTypeFilter] = useState<"" | "rechnung" | "stripe" | "cash">("");
    // С какого сайта заказ: все / основной / второй сайт (UA).
    const [siteSourceFilter, setSiteSourceFilter] = useState<"" | "main" | "ua">("");
    // Продано у партнёра (пометка PARTNER): все / партнёрские / Nashe / Artist Production / не партнёрские.
    const [partnerFilter, setPartnerFilter] = useState<"" | "yes" | "no" | "nashe" | "ap">("");
    const [customerTypeFilter, setCustomerTypeFilter] = useState<"" | "agents" | "clients">("");
    const [selectedAgentId, setSelectedAgentId] = useState<string>("");
    const [agents, setAgents] = useState<UserItem[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsLoaded, setAgentsLoaded] = useState(false);
    const [agentsError, setAgentsError] = useState<string | null>(null);

    // фильтр по туру и (при выборе тура) по городу тура
    const [selectedTourId, setSelectedTourId] = useState<string>("");
    const [tours, setTours] = useState<TourItem[]>([]);
    const [toursLoading, setToursLoading] = useState(false);
    const [toursLoaded, setToursLoaded] = useState(false);
    const [toursError, setToursError] = useState<string | null>(null);

    const [selectedTourCityId, setSelectedTourCityId] = useState<string>("");
    const [tourCities, setTourCities] = useState<TourCityItem[]>([]);
    const [tourCitiesLoading, setTourCitiesLoading] = useState(false);
    const [tourCitiesError, setTourCitiesError] = useState<string | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);

    const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
    const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);

    // Модальное окно "Применить скидку" (только для неоплаченных заказов)
    const [discountModalOrder, setDiscountModalOrder] = useState<OrderItem | null>(null);
    const [orderToDelete, setOrderToDelete] = useState<OrderItem | null>(null);
    // Удаление оплаченного у партнёра заказа: галочка «партнёр подтвердил удаление у себя».
    const [partnerDeletionConfirmed, setPartnerDeletionConfirmed] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [orderToRefund, setOrderToRefund] = useState<OrderItem | null>(null);
    // Удалённый заказ, который админ хочет восстановить (окно проверки мест).
    const [orderToRestore, setOrderToRestore] = useState<OrderItem | null>(null);
    const [orderToDeliver, setOrderToDeliver] = useState<OrderItem | null>(null);

    // Модальное окно билетов
    const [ticketsModalOrder, setTicketsModalOrder] = useState<OrderItem | null>(null);
    // Комментарий к заказу: модалка добавления/редактирования
    const [commentOrder, setCommentOrder] = useState<OrderItem | null>(null);
    const [commentDraft, setCommentDraft] = useState("");
    const [isSavingComment, setIsSavingComment] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);
    // Всплывающая подсказка с полным текстом комментария (position: fixed — не обрезается таблицей)
    const [commentTip, setCommentTip] = useState<{ text: string; x: number; y: number; above: boolean } | null>(null);

    const openCommentEditor = (order: OrderItem) => {
        setCommentTip(null);
        setCommentError(null);
        setCommentDraft(order.comment || "");
        setCommentOrder(order);
    };

    const showCommentTip = (event: React.MouseEvent<HTMLElement>, text?: string | null) => {
        if (!text || !text.trim()) return;
        const rect = event.currentTarget.getBoundingClientRect();
        // У нижнего края окна показываем подсказку над иконкой; по горизонтали не выходим за экран.
        const above = rect.bottom + 180 > window.innerHeight;
        const x = Math.min(Math.max(rect.left + rect.width / 2, 190), window.innerWidth - 190);
        setCommentTip({ text, x, y: above ? rect.top - 8 : rect.bottom + 8, above });
    };
    const hideCommentTip = () => setCommentTip(null);

    const handleSaveComment = async () => {
        if (!commentOrder) return;
        setIsSavingComment(true);
        setCommentError(null);
        try {
            const value = commentDraft.trim();
            const updated = await updateOrderRequest(commentOrder.id, { comment: value });
            // В ответе PATCH нет части полей списка (tours и т.п.) — обновляем только комментарий.
            setItems((prev) =>
                prev.map((o) => (o.id === commentOrder.id ? { ...o, comment: updated.comment ?? value } : o)),
            );
            setCommentOrder(null);
        } catch (err: any) {
            setCommentError(err?.message || "Не удалось сохранить комментарий");
        } finally {
            setIsSavingComment(false);
        }
    };

    // Модальное окно "какие места и какие скидки применены" (только для discount_kind === "rabatt")
    const [discountsModalOrder, setDiscountsModalOrder] = useState<OrderItem | null>(null);

    // Отправка (счёт/билеты/лифершайн) — общие состояния
    const [sendingOrderId, setSendingOrderId] = useState<string | null>(null);
    const [sendMessage, setSendMessage] = useState<string | null>(null);
    const [sendError, setSendError] = useState<string | null>(null);

    // Адресная наклейка «Доставка» (.docx, редактируется в Word) — адрес берётся из заказа.
    const [deliveryDocxOrderId, setDeliveryDocxOrderId] = useState<string | null>(null);
    const handleDownloadDeliveryDocx = async (order: OrderItem) => {
        if (deliveryDocxOrderId) return;
        setSendMessage(null);
        setSendError(null);
        setDeliveryDocxOrderId(order.id);
        try {
            const { blob, filename } = await downloadOrderDeliveryDocxRequest(order.id);
            const name = filename.endsWith(".docx") ? filename : `Lieferung_${order.order_number}.docx`;
            triggerBrowserDownload(blob, name);
        } catch (err: any) {
            setSendError(err?.message || "Не удалось сформировать файл доставки");
        } finally {
            setDeliveryDocxOrderId(null);
        }
    };

    // Письмо покупателю по заказу: модалка «тема / текст» -> POST /orders/{id}/send-email.
    const [emailOrder, setEmailOrder] = useState<OrderItem | null>(null);
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [emailSending, setEmailSending] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);

    const handleOpenEmailModal = (order: OrderItem) => {
        setEmailOrder(order);
        setEmailSubject(`Ihre Bestellung ${order.order_number}`);
        setEmailBody("");
        setEmailError(null);
    };

    const handleSendOrderEmail = async () => {
        if (!emailOrder) return;
        if (!emailSubject.trim() || !emailBody.trim()) {
            setEmailError("Заполните тему и текст письма");
            return;
        }
        setEmailSending(true);
        setEmailError(null);
        try {
            const result = await sendOrderEmailRequest(emailOrder.id, emailSubject.trim(), emailBody.trim());
            setSendError(null);
            setSendMessage(`Письмо по заказу ${emailOrder.order_number} отправлено на ${result.sent_to}`);
            setEmailOrder(null);
        } catch (err: any) {
            setEmailError(err?.message || "Не удалось отправить письмо");
        } finally {
            setEmailSending(false);
        }
    };

    const runSend = async (
        order: OrderItem,
        action: "invoice" | "tickets" | "lieferschein",
    ) => {
        setSendMessage(null);
        setSendError(null);
        setSendingOrderId(order.id);
        try {
            if (action === "invoice") {
                await sendOrderInvoiceRequest(order.id);
                setSendMessage(`Счёт по заказу ${order.order_number} отправлен`);
            } else if (action === "tickets") {
                await sendOrderTicketsRequest(order.id);
                setSendMessage(`Билеты по заказу ${order.order_number} отправлены`);
            } else {
                await sendOrderLieferscheinRequest(order.id);
                setSendMessage(`Lieferschein по заказу ${order.order_number} отправлен`);
            }
        } catch (err: any) {
            setSendError(err?.message || "Не удалось выполнить отправку");
        } finally {
            setSendingOrderId(null);
        }
    };

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(totalSize / PAGE_SIZE)),
        [totalSize],
    );

    const isAgentFilterValue =
        customerTypeFilter === "agents"
            ? true
            : customerTypeFilter === "clients"
                ? false
                : undefined;

    // защита от гонки запросов: если ответ на устаревший запрос приходит позже более нового,
    // он игнорируется и не затирает свежие данные.
    const requestSeqRef = useRef(0);

    // Текущий фильтр списка — один и тот же для загрузки страницы и выгрузки в Excel.
    const buildCurrentFilters = (): ListOrdersFilters => ({
        order_number: debouncedOrderNumber.trim() || undefined,
        customer_name: debouncedCustomerName.trim() || undefined,
        customer_email: debouncedCustomerEmail.trim() || undefined,
        customer_phone: debouncedCustomerPhone.trim() || undefined,
        invoice_number: debouncedInvoiceNumber.trim() || undefined,
        comment: debouncedCommentSearch.trim() || undefined,
        // дата от — начало суток (00:00:00.000), дата до — конец суток (23:59:59.999),
        // чтобы одинаковые даты в "дата от"/"дата до" охватывали весь день целиком.
        // Возврат к предыдущему рабочему варианту — конвертируем через toISOString().
        // Важно: по сырому ответу API выяснилось, что Order.created_at возвращается
        // backend'ом как полноценный момент времени с корректным берлинским offset'ом
        // (например, "...T23:12:19+02:00"), а НЕ как наивные берлинские цифры — попытка
        // убрать toISOString() и отправлять наивную строку была ошибой (стала возвращать
        // на 1 заказ меньше). Актуальная причина расхождения Заказы/Статистика —
        // не в этой строке, а в том, что верстка конкретного заказа по берлинскому
        // времени действительно пришлась на предыдущий календарный день (см. обсуждение с пользователем).
        date_from: dateFrom ? new Date(`${dateFrom}T00:00:00.000`).toISOString() : undefined,
        date_to: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined,
        is_paid: isPaidFilter === "" ? undefined : isPaidFilter === "true",
        is_deleted: isDeletedFilter === "" ? undefined : isDeletedFilter === "true",
        need_delivery: deliveryFilter === "" ? undefined : deliveryFilter === "true",
        // «C» в списке — и наличные (cash), и наложенный платёж (cod).
        payment_type:
            paymentTypeFilter === "cash" ? "cash,cod" : paymentTypeFilter || undefined,
        site_source: siteSourceFilter || undefined,
        partner: partnerFilter || undefined,
        is_agent: isAgentFilterValue,
        customer_id:
            customerTypeFilter === "agents" && selectedAgentId
                ? selectedAgentId
                : undefined,
        tour_id: selectedTourId || undefined,
        tour_city_id: selectedTourId && selectedTourCityId ? selectedTourCityId : undefined,
    });

    // Выгрузка в Excel всех заказов по текущему фильтру, сгруппированных по клиентам.
    const [isExporting, setIsExporting] = useState(false);
    const handleExportExcel = async () => {
        setSendMessage(null);
        setSendError(null);
        setIsExporting(true);
        try {
            const { blob, filename } = await downloadOrdersGroupedExcelRequest(buildCurrentFilters());
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            setSendError(err?.message || "Не удалось выгрузить заказы в Excel");
        } finally {
            setIsExporting(false);
        }
    };

    const loadData = async () => {
        const seq = ++requestSeqRef.current;
        setIsLoading(true);
        setPageError(null);
        try {
            const response = await getOrdersRequest(page, PAGE_SIZE, buildCurrentFilters());
            if (seq !== requestSeqRef.current) {
                return; // пришёл устаревший ответ — игнорируем
            }
            setItems(response.items);
            setTotalSize(response.total_size);
        } catch (error) {
            if (seq !== requestSeqRef.current) {
                return;
            }
            setPageError(error instanceof Error ? error.message : "Ошибка загрузки заказов");
        } finally {
            if (seq === requestSeqRef.current) {
                setIsLoading(false);
            }
        }
    };

    // Дебаунс текстовых полей фильтра: запрос к серверу уходит спустя паузу в наборе текста,
    // а не на каждое нажатие клавиши — иначе при быстром вводе запросы накладываются друг на друга
    // и кажется, что фильтрация не работает.
    const DEBOUNCE_MS = 400;
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedOrderNumber(orderNumber);
            setDebouncedCustomerName(customerName);
            setDebouncedCustomerEmail(customerEmail);
            setDebouncedCustomerPhone(customerPhoneSearch);
            setDebouncedInvoiceNumber(invoiceNumber);
            setDebouncedCommentSearch(commentSearch);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [orderNumber, customerName, customerEmail, customerPhoneSearch, invoiceNumber, commentSearch]);

    // Единый ключ всех фильтров (кроме страницы): при любом его изменении сбрасываем
    // страницу на первую — независимо от того, какой именно фильтр поменялся.
    const filtersKey = useMemo(
        () =>
            JSON.stringify([
                debouncedOrderNumber,
                debouncedCustomerName,
                debouncedCustomerEmail,
                debouncedCustomerPhone,
                debouncedInvoiceNumber,
                debouncedCommentSearch,
                dateFrom,
                dateTo,
                isPaidFilter,
                isDeletedFilter,
                deliveryFilter,
                paymentTypeFilter,
                siteSourceFilter,
                partnerFilter,
                customerTypeFilter,
                selectedAgentId,
                selectedTourId,
                selectedTourCityId,
            ]),
        [
            debouncedOrderNumber,
            debouncedCustomerName,
            debouncedCustomerEmail,
            debouncedCustomerPhone,
            debouncedInvoiceNumber,
            debouncedCommentSearch,
            dateFrom,
            dateTo,
            isPaidFilter,
            isDeletedFilter,
            deliveryFilter,
            paymentTypeFilter,
            siteSourceFilter,
            partnerFilter,
            customerTypeFilter,
            selectedAgentId,
            selectedTourId,
            selectedTourCityId,
        ],
    );
    const prevFiltersKeyRef = useRef(filtersKey);

    useEffect(() => {
        if (prevFiltersKeyRef.current !== filtersKey) {
            prevFiltersKeyRef.current = filtersKey;
            if (page !== 1) {
                // Смена любого фильтра — всегда возвращаемся на первую страницу;
                // сама загрузка запустится повторно этим же эффектом при изменении page.
                setPage(1);
                return;
            }
        }
        void loadData();
    }, [filtersKey, page]);

    // Список туров для селекта фильтра — грузим один раз при открытии страницы.
    useEffect(() => {
        if (toursLoaded) {
            return;
        }
        let cancelled = false;
        setToursLoading(true);
        setToursError(null);
        (async () => {
            try {
                const response = await getToursRequest({ page: 1, size: 200 });
                if (cancelled) return;
                const list: TourItem[] = Array.isArray(response)
                    ? response
                    : response.items ?? response.data ?? response.results ?? [];
                setTours(list);
                setToursLoaded(true);
            } catch (error) {
                if (cancelled) return;
                setTours([]);
                setToursLoaded(true);
                setToursError(error instanceof Error ? error.message : "Ошибка загрузки туров");
            } finally {
                if (!cancelled) setToursLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [toursLoaded]);

    // При выборе тура — грузим его города (даты/площадки) для второго селекта.
    useEffect(() => {
        if (!selectedTourId) {
            setTourCities([]);
            setTourCitiesError(null);
            return;
        }
        let cancelled = false;
        setTourCitiesLoading(true);
        setTourCitiesError(null);
        (async () => {
            try {
                const response = await getTourCitiesRequest(selectedTourId);
                if (cancelled) return;
                setTourCities(Array.isArray(response) ? response : []);
            } catch (error) {
                if (cancelled) return;
                setTourCities([]);
                setTourCitiesError(error instanceof Error ? error.message : "Ошибка загрузки городов тура");
            } finally {
                if (!cancelled) setTourCitiesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedTourId]);

    // Список агентов для второго селекта — подгружаем один раз, когда выбраны "Заказы агентов".
    useEffect(() => {
        if (customerTypeFilter !== "agents") {
            return;
        }
        if (agentsLoaded) {
            return;
        }

        let cancelled = false;
        setAgentsLoading(true);
        setAgentsError(null);
        (async () => {
            try {
                const response = await getUsersRequest({
                    is_agent: true,
                    role: "customer",
                    page: 1,
                    page_size: 100,
                    sort_by: "first_name",
                });
                if (cancelled) return;
                // На всякий случай поддерживаем разные варианты формата ответа.
                const raw: any = response as any;
                const list: UserItem[] = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.items)
                        ? raw.items
                        : Array.isArray(raw?.users)
                            ? raw.users
                            : Array.isArray(raw?.data)
                                ? raw.data
                                : [];
                // eslint-disable-next-line no-console
                console.log("[OrdersPage] agents loaded:", list.length, raw);
                setAgents(list);
                setAgentsLoaded(true);
            } catch (error) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error("[OrdersPage] agents load error:", error);
                setAgents([]);
                setAgentsLoaded(true);
                setAgentsError(
                    error instanceof Error
                        ? error.message
                        : "Ошибка загрузки агентов",
                );
            } finally {
                if (!cancelled) setAgentsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [customerTypeFilter, agentsLoaded]);

    const handleCustomerTypeChange = (
        value: "" | "agents" | "clients",
    ) => {
        setCustomerTypeFilter(value);
        setSelectedAgentId("");
        setPage(1);
    };

    const handleAgentChange = (value: string) => {
        setSelectedAgentId(value);
        setPage(1);
    };

    const handleTourChange = (value: string) => {
        setSelectedTourId(value);
        setSelectedTourCityId("");
        setPage(1);
    };

    const handleTourCityChange = (value: string) => {
        setSelectedTourCityId(value);
        setPage(1);
    };

    const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // По Enter/submit не ждём debounce текстовых полей — сразу выталкиваем их в debounced-значения.
        setDebouncedOrderNumber(orderNumber);
        setDebouncedCustomerName(customerName);
        setDebouncedCustomerEmail(customerEmail);
        setDebouncedCustomerPhone(customerPhoneSearch);
        setDebouncedInvoiceNumber(invoiceNumber);
        setDebouncedCommentSearch(commentSearch);
        setPage(1);
        // дальше всё сделает useEffect по filtersKey/page
    };

    const handleOpenAddPayment = (order: OrderItem) => {
        setSelectedOrder(order);
        setIsAddPaymentOpen(true);
    };

    const handlePaymentAdded = (paymentAmount: number, comment: string) => {
        if (!selectedOrder) return;
        const nextPaid = Number(selectedOrder.paid_amount) + paymentAmount;
        const isFullyPaid = nextPaid >= Number(selectedOrder.total_price);

        const updated: OrderItem = {
            ...selectedOrder,
            paid_amount: nextPaid.toFixed(2),
            is_paid: isFullyPaid,
            // Время оплаты точно запишет API; здесь — для мгновенного отображения в списке.
            paid_at: isFullyPaid ? selectedOrder.paid_at ?? new Date().toISOString() : selectedOrder.paid_at,
            payments: [
                {
                    id: "temp",
                    order_id: selectedOrder.id,
                    amount: Number(paymentAmount),
                    payment_type: "rechnung",
                    comment,
                    paid_at: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                },
                ...selectedOrder.payments,
            ],
        };

        setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
        setSelectedOrder(updated);
    };

    const openDeleteModal = (order: OrderItem) => {
        setPartnerDeletionConfirmed(false);
        setDeleteError(null);
        setOrderToDelete(order);
    };

    const closeDeleteModal = () => {
        if (isDeleting) return;
        setOrderToDelete(null);
        setPartnerDeletionConfirmed(false);
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        if (!orderToDelete) return;
        const partnerInfo = getPartnerOrderInfo(orderToDelete);
        const needsPartnerConfirmation = Boolean(partnerInfo?.paidAtPartner);
        if (needsPartnerConfirmation && !partnerDeletionConfirmed) {
            setDeleteError("Отметьте, что партнёр подтвердил удаление заказа у себя.");
            return;
        }
        setIsDeleting(true);
        setDeleteError(null);
        try {
            const updated = await updateOrderRequest(orderToDelete.id, {
                is_deleted: true,
                ...(needsPartnerConfirmation ? { partner_deletion_confirmed: true } : {}),
            });
            setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
            setOrderToDelete(null);
            setPartnerDeletionConfirmed(false);
        } catch (error: any) {
            // Партнёрский заказ: ошибку показываем в окне (например, партнёр не отменил резерв).
            setDeleteError(error?.message || "Не удалось удалить заказ");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleConfirmRefund = async () => {
        if (!orderToRefund) return;
        try {
            const updated = await updateOrderRequest(orderToRefund.id, { is_refunded: true });
            setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
        } catch (error) {
            // алерт
        } finally {
            setOrderToRefund(null);
        }
    };

    const handleConfirmDelivery = async () => {
        if (!orderToDeliver) {
            return;
        }

        try {
            const updated = await updateOrderRequest(orderToDeliver.id, { is_printed: true });
            setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
        } catch (error) {
        } finally {
            setOrderToDeliver(null);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Заказы"
                description="Управление заказами, оплатами и возвратами."
                actions={
                    <Button variant="secondary" onClick={handleExportExcel} disabled={isExporting}>
                        {isExporting ? "Готовим Excel…" : "Скачать Excel по фильтру"}
                    </Button>
                }
            />

            {sendMessage && (
                <div
                    className="form-global-success"
                    style={{
                        marginBottom: 12,
                        padding: "10px 14px",
                        borderRadius: 8,
                        background: "#ecfdf5",
                        color: "#065f46",
                        border: "1px solid #a7f3d0",
                    }}
                >
                    {sendMessage}
                </div>
            )}
            {sendError && (
                <div
                    className="form-global-error"
                    style={{
                        marginBottom: 12,
                        padding: "10px 14px",
                        borderRadius: 8,
                        background: "#fef2f2",
                        color: "#991b1b",
                        border: "1px solid #fecaca",
                    }}
                >
                    {sendError}
                </div>
            )}

            <Card>
                <section className="section-toolbar">
                    <div>
                        <h3 className="card-title">Список заказов</h3>
                        <p className="card-text">
                            Фильтруйте по датам, номеру заказа и статусам.
                        </p>
                    </div>

                    <form className="orders-filter-bar" onSubmit={handleFilterSubmit}>
                        <div className="orders-filter-row">
                            <Field label="Номер" htmlFor="order-number">
                                <Input
                                    id="order-number"
                                    type="text"
                                    value={orderNumber}
                                    placeholder="ORD-..."
                                    onChange={(e) => setOrderNumber(e.target.value)}
                                />
                            </Field>

                            <Field label="Имя клиента" htmlFor="order-customer-name">
                                <Input
                                    id="order-customer-name"
                                    type="text"
                                    value={customerName}
                                    placeholder="Имя или фамилия"
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                            </Field>

                            <Field label="Email клиента" htmlFor="order-customer-email">
                                <Input
                                    id="order-customer-email"
                                    type="text"
                                    value={customerEmail}
                                    placeholder="email@example.com"
                                    onChange={(e) => setCustomerEmail(e.target.value)}
                                />
                            </Field>

                            <Field label="Телефон клиента" htmlFor="order-customer-phone">
                                <Input
                                    id="order-customer-phone"
                                    type="text"
                                    value={customerPhoneSearch}
                                    placeholder="+49 171 1234567"
                                    onChange={(e) => setCustomerPhoneSearch(e.target.value)}
                                />
                            </Field>

                            <Field label="Номер счёта" htmlFor="order-invoice-number">
                                <Input
                                    id="order-invoice-number"
                                    type="text"
                                    value={invoiceNumber}
                                    placeholder="RE-ORD-000123"
                                    onChange={(e) => setInvoiceNumber(e.target.value)}
                                />
                            </Field>

                            <Field label="Комментарий" htmlFor="order-comment-search">
                                <Input
                                    id="order-comment-search"
                                    type="text"
                                    value={commentSearch}
                                    placeholder="Текст из комментария"
                                    onChange={(e) => setCommentSearch(e.target.value)}
                                />
                            </Field>

                            <Field label="Дата от" htmlFor="order-date-from">
                                <Input
                                    id="order-date-from"
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </Field>

                            <Field label="Дата до" htmlFor="order-date-to">
                                <Input
                                    id="order-date-to"
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </Field>

                            <Field label="Оплачен" htmlFor="order-is-paid">
                                <select
                                    id="order-is-paid"
                                    className="ui-select"
                                    value={isPaidFilter}
                                    onChange={(e) =>
                                        setIsPaidFilter(e.target.value as "" | "true" | "false")
                                    }
                                >
                                    <option value="">Все</option>
                                    <option value="true">Да</option>
                                    <option value="false">Нет</option>
                                </select>
                            </Field>

                            <Field label="Удалён" htmlFor="order-is-deleted">
                                <select
                                    id="order-is-deleted"
                                    className="ui-select"
                                    value={isDeletedFilter}
                                    onChange={(e) =>
                                        setIsDeletedFilter(e.target.value as "" | "true" | "false")
                                    }
                                >
                                    <option value="">Все</option>
                                    <option value="true">Да</option>
                                    <option value="false">Нет</option>
                                </select>
                            </Field>

                            <Field label="Доставка" htmlFor="order-delivery">
                                <select
                                    id="order-delivery"
                                    className="ui-select"
                                    value={deliveryFilter}
                                    onChange={(e) =>
                                        setDeliveryFilter(e.target.value as "" | "true" | "false")
                                    }
                                >
                                    <option value="">Все</option>
                                    <option value="true">Есть</option>
                                    <option value="false">Нет</option>
                                </select>
                            </Field>

                            <Field label="Оплата" htmlFor="order-payment-type">
                                <select
                                    id="order-payment-type"
                                    className="ui-select"
                                    value={paymentTypeFilter}
                                    onChange={(e) =>
                                        setPaymentTypeFilter(
                                            e.target.value as "" | "rechnung" | "stripe" | "cash",
                                        )
                                    }
                                    title="R — счёт (Rechnung), S — онлайн (Stripe), C — наличные / наложенный платёж"
                                >
                                    <option value="">Все</option>
                                    <option value="rechnung">R — счёт</option>
                                    <option value="stripe">S — Stripe</option>
                                    <option value="cash">C — наличные</option>
                                </select>
                            </Field>

                            <Field label="Сайт" htmlFor="order-site-source">
                                <select
                                    id="order-site-source"
                                    className="ui-select"
                                    value={siteSourceFilter}
                                    onChange={(e) => setSiteSourceFilter(e.target.value as "" | "main" | "ua")}
                                >
                                    <option value="">Все</option>
                                    <option value="main">Основной</option>
                                    <option value="ua">UA-сайт</option>
                                </select>
                            </Field>

                            <Field label="Партнёр" htmlFor="order-partner">
                                <select
                                    id="order-partner"
                                    className="ui-select"
                                    value={partnerFilter}
                                    onChange={(e) =>
                                        setPartnerFilter(e.target.value as "" | "yes" | "no" | "nashe" | "ap")
                                    }
                                >
                                    <option value="">Все</option>
                                    <option value="yes">PARTNER</option>
                                    <option value="nashe">Nashe</option>
                                    <option value="ap">Artist Prod.</option>
                                    <option value="no">Не партнёрские</option>
                                </select>
                            </Field>

                            <Field label="Покупатели" htmlFor="order-customer-type">
                                <select
                                    id="order-customer-type"
                                    className="ui-select"
                                    value={customerTypeFilter}
                                    onChange={(e) =>
                                        handleCustomerTypeChange(
                                            e.target.value as "" | "agents" | "clients",
                                        )
                                    }
                                >
                                    <option value="">Все заказы</option>
                                    <option value="agents">Заказы агентов</option>
                                    <option value="clients">Заказы клиентов</option>
                                </select>
                            </Field>

                            {customerTypeFilter === "agents" && (
                                <Field label="Агент" htmlFor="order-agent">
                                    <select
                                        id="order-agent"
                                        className="ui-select"
                                        value={selectedAgentId}
                                        onChange={(e) => handleAgentChange(e.target.value)}
                                        disabled={agentsLoading}
                                    >
                                        <option value="">
                                            {agentsLoading
                                                ? "Загрузка..."
                                                : agentsError
                                                    ? "Ошибка загрузки"
                                                    : agents.length === 0 && agentsLoaded
                                                        ? "Агенты не найдены"
                                                        : "Все агенты"}
                                        </option>
                                        {agents.map((agent) => {
                                            const fullName = [agent.first_name, agent.last_name]
                                                .filter(Boolean)
                                                .join(" ")
                                                .trim();
                                            const label = fullName
                                                ? `${fullName} (${agent.email})`
                                                : agent.email;
                                            return (
                                                <option key={agent.id} value={agent.id}>
                                                    {label}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </Field>
                            )}

                            <Field label="Тур" htmlFor="order-tour">
                                <select
                                    id="order-tour"
                                    className="ui-select"
                                    value={selectedTourId}
                                    onChange={(e) => handleTourChange(e.target.value)}
                                    disabled={toursLoading}
                                >
                                    <option value="">
                                        {toursLoading
                                            ? "Загрузка..."
                                            : toursError
                                                ? "Ошибка загрузки"
                                                : "Все туры"}
                                    </option>
                                    {tours.map((tour) => (
                                        <option key={tour.id} value={tour.id}>
                                            {tour.name_ru || tour.name_en || tour.name_de || tour.id}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            {selectedTourId && (
                                <Field label="Город тура" htmlFor="order-tour-city">
                                    <select
                                        id="order-tour-city"
                                        className="ui-select"
                                        value={selectedTourCityId}
                                        onChange={(e) => handleTourCityChange(e.target.value)}
                                        disabled={tourCitiesLoading}
                                    >
                                        <option value="">
                                            {tourCitiesLoading
                                                ? "Загрузка..."
                                                : tourCitiesError
                                                    ? "Ошибка загрузки"
                                                    : tourCities.length === 0
                                                        ? "Города не найдены"
                                                        : "Все города"}
                                        </option>
                                        {tourCities.map((tourCity) => {
                                            const cityName =
                                                tourCity.city?.name_ru ||
                                                tourCity.city?.name_en ||
                                                tourCity.city?.name_de ||
                                                "Город не указан";
                                            const dateLabel = tourCity.start_at
                                                ? new Date(tourCity.start_at).toLocaleDateString("ru-RU")
                                                : "";
                                            return (
                                                <option key={tourCity.id} value={tourCity.id}>
                                                    {dateLabel ? `${cityName} — ${dateLabel}` : cityName}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </Field>
                            )}
                        </div>
                    </form>
                </section>

                {pageError && <div className="form-global-error">{pageError}</div>}

                {isLoading ? (
                    <div className="table-skeleton">
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="empty-block">
                        <h3 className="empty-block-title">Заказов нет</h3>
                        <p className="empty-block-text">
                            Попробуйте изменить фильтры или период.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Заказ</th>
                                    <th>Покупатель</th>
                                    <th>Создал</th>
                                    <th>Событие</th>
                                    <th>Сумма</th>
                                    <th>Доставка</th>
                                    <th>Оплата</th>
                                    <th>Действия</th>
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((order) => {
                                    const remaining =
                                        Number(order.total_price) - Number(order.paid_amount);
                                    const isDeleted = order.is_deleted;
                                    const isRefunded = order.is_refunded;
                                    const needDelivery = order.need_delivery;
                                    const customerInfo = order.customer_info;
                                    const apiDistributorInfo = order.api_distributor_info;
                                    const isApiOrder = !!(order.is_api_order || apiDistributorInfo);

                                    const customerFallbackName = isApiOrder
                                        ? (apiDistributorInfo?.name || "Распространитель")
                                        : "Покупатель не указан";
                                    const customerFullName =
                                        [customerInfo?.first_name, customerInfo?.last_name]
                                            .filter(Boolean)
                                            .join(" ")
                                            .trim() || customerFallbackName;

                                    const customerPhone =
                                        customerInfo?.phone?.trim() ||
                                        (isApiOrder ? apiDistributorInfo?.contact_phone?.trim() || "Телефон не указан" : "Телефон не указан");
                                    const customerEmail =
                                        customerInfo?.email?.trim() ||
                                        (isApiOrder ? apiDistributorInfo?.email?.trim() || "Email не указан" : "Email не указан");

                                    // Инфо о создателе заказа: всегда показываем имя и email. Если создатель совпадает с покупателем —
                                    // в колонке просто дублируются данные покупателя, без надписи «Клиент».
                                    const createdByInfo = order.created_by_info;
                                    const createdByName =
                                        [createdByInfo?.first_name, createdByInfo?.last_name]
                                            .filter(Boolean)
                                            .join(" ")
                                            .trim();
                                    const createdBySelf =
                                        !!order.created_by_id &&
                                        !!order.customer_id &&
                                        order.created_by_id === order.customer_id;
                                    const createdByLabel = createdByName || "—";
                                    const createdByEmail = createdByInfo?.email?.trim() || "";

                                    const deliveryAddress = [order?.zip_code, order?.country, order?.city, order?.address]
                                        .filter(Boolean)
                                        .join(", ");

                                    return (
                                        <tr
                                            key={order.id}
                                            className={
                                                selectedOrder?.id === order.id
                                                    ? "data-table-row-active"
                                                    : undefined
                                            }
                                        >
                                            <td>
                                                <div className="table-primary-cell">
                            <span
                                className="table-primary-text"
                                style={{
                                    cursor: "pointer",
                                    color: "#60a5fa",
                                    textDecoration: "underline",
                                }}
                                onClick={() => setTicketsModalOrder(order)}
                                title="Открыть билеты заказа"
                            >
                              {order.order_number}
                            </span>
                                                    {order.site_source && order.site_source !== "main" ? (
                                                        <span
                                                            className="site-source-badge"
                                                            title="Заказ оформлен на втором сайте (заголовок X-Site-Source)"
                                                        >
                                                            {order.site_source.toUpperCase()}-сайт
                                                        </span>
                                                    ) : null}
                                                    {order.invoice_number != null && (
                                                        <span className="table-secondary-text">
                                {`RE-ORD-${String(order.invoice_number).padStart(6, "0")}`}
                              </span>
                                                    )}
                                                    <span className="table-secondary-text">
                              {new Date(order.created_at).toLocaleString()}
                            </span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="table-primary-cell">
                                                    <span
                                                        className="table-primary-text"
                                                        style={{display: "inline-flex", alignItems: "center", gap: 6}}
                                                    >
                                                        {customerFullName}
                                                        {order.comment && order.comment.trim() ? (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openCommentEditor(order);
                                                                }}
                                                                onMouseEnter={(e) => showCommentTip(e, order.comment)}
                                                                onMouseLeave={hideCommentTip}
                                                                aria-label="Комментарий к заказу"
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    width: 22,
                                                                    height: 22,
                                                                    borderRadius: "50%",
                                                                    background: "#eef2ff",
                                                                    color: "#4338ca",
                                                                    border: "1px solid #c7d2fe",
                                                                    cursor: "pointer",
                                                                    fontSize: 13,
                                                                    lineHeight: 1,
                                                                    padding: 0,
                                                                }}
                                                            >
                                                                💬
                                                            </button>
                                                        ) : null}
                                                        {isApiOrder && (
                                                            <>
                                                                &nbsp;&nbsp;
                                                                <span
                                                                    className="status-badge"
                                                                    style={{
                                                                        background: "#7c3aed",
                                                                        color: "#fff",
                                                                        borderColor: "#7c3aed",
                                                                        letterSpacing: 0.5,
                                                                    }}
                                                                    title={apiDistributorInfo?.name || "Заказ создан через API распространителя"}
                                                                >
                                                                    API
                                                                </span>
                                                            </>
                                                        )}
                                                        {order.is_partner_order && (
                                                            <>
                                                                &nbsp;&nbsp;
                                                                <span
                                                                    className="status-badge"
                                                                    style={{
                                                                        background: "#0891b2",
                                                                        color: "#fff",
                                                                        borderColor: "#0891b2",
                                                                        letterSpacing: 0.5,
                                                                    }}
                                                                    title={
                                                                        order.nashe_data
                                                                            ? "Партнёрский тур продан по API Nashe"
                                                                            : "Партнёрский тур продан по API Artist Production"
                                                                    }
                                                                >
                                                                    PARTNER
                                                                </span>
                                                            </>
                                                        )}

                                                    </span>

                                                    <span className="table-secondary-text">
                                                        {customerPhone} | {customerEmail}
                                                        {customerInfo?.email?.trim() &&
                                                        customerInfo.email.includes("@") &&
                                                        !customerInfo.email.trim().endsWith("@showmust.local") ? (
                                                            <button
                                                                type="button"
                                                                className="icon-button"
                                                                title={`Написать письмо на ${customerInfo.email.trim()}`}
                                                                aria-label="Написать письмо"
                                                                onClick={() => handleOpenEmailModal(order)}
                                                                style={{
                                                                    marginLeft: 6,
                                                                    width: 24,
                                                                    height: 24,
                                                                    padding: 0,
                                                                    verticalAlign: "middle",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                <Mail size={14} />
                                                            </button>
                                                        ) : null}
                                                    </span>

                                                    {(() => {
                                                        const partnerInfo = getPartnerOrderInfo(order);
                                                        if (!partnerInfo) return null;
                                                        return (
                                                            <span className="table-secondary-text partner-order-ref">
                                                                Партнёр: {partnerInfo.partnerName}, {partnerInfo.reference}
                                                                {order.is_deleted
                                                                    ? " (заказ отменён)"
                                                                    : partnerInfo.paidAtPartner
                                                                        ? " (оплачен у партнёра)"
                                                                        : " (резерв)"}
                                                            </span>
                                                        );
                                                    })()}

                                                    {order.need_delivery && deliveryAddress && (
                                                        <span className="table-secondary-text">
        Адрес доставки: {deliveryAddress}
      </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="table-primary-cell">
                                                    <span className="table-primary-text">
                                                        {createdByLabel}
                                                        {!createdBySelf && createdByInfo?.role === "admin" && (
                                                            <>
                                                                &nbsp;&nbsp;
                                                                <span className="status-badge status-badge-active">Админ</span>
                                                            </>
                                                        )}
                                                        {createdByInfo?.is_agent && createdByInfo?.role !== "admin" && (
                                                            <>
                                                                &nbsp;&nbsp;
                                                                <span className="status-badge status-badge-active">Распр.</span>
                                                            </>
                                                        )}
                                                    </span>
                                                    {createdByEmail && (
                                                        <span className="table-secondary-text">{createdByEmail}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="table-primary-cell">
                                                    {order.tours.map((tour, tourIndex) => (
                                                        <React.Fragment key={tour.tour_city_id ?? tourIndex}>

                                                            <span className="table-primary-text">{tour.tour_name_ru}</span> <span className="table-secondary-text"><b style={{color: "#f59e0b"}}>{tour.city_name_ru}</b> Билетов {tour.items.length}</span>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="table-primary-cell">
                                                    {order.discount_kind === "invite" && (
                                                        <span
                                                            style={{
                                                                display: "inline-block",
                                                                background: "#fde68a",
                                                                color: "#92400e",
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                padding: "2px 8px",
                                                                borderRadius: 6,
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            ПРИГЛАСИТЕЛЬНЫЙ
                                                        </span>
                                                    )}
                                                    {Number(order.subscription_discount_amount) > 0 && (
                                                        <span
                                                            title="У покупателя действует годовой абонемент клуба — скидка применена к этому заказу"
                                                            style={{
                                                                display: "inline-block",
                                                                background: "#ede9fe",
                                                                color: "#6128ea",
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                padding: "2px 8px",
                                                                borderRadius: 6,
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            АБОНЕМЕНТ {Number(order.subscription_discount_percent) || 0}%
                                                        </span>
                                                    )}
                                                    <span className="table-primary-text">
                                                      {order.total_price} €
                                                        {order.discount_value > 0 && order.discount_kind !== "invite" && (
                                                            order.discount_kind === "rabatt"
                                                            || Number(order.subscription_discount_amount) > 0 ? (
                                                                <span
                                                                    className="table-secondary-text"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() => setDiscountsModalOrder(order)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            setDiscountsModalOrder(order);
                                                                        }
                                                                    }}
                                                                    title="Показать расшифровку скидки: места, скидки по билетам и годовой абонемент"
                                                                    style={{
                                                                        color: "#22c55e",
                                                                        fontWeight: 700,
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                    }}
                                                                >
                                                                    &nbsp;&nbsp;Скидка {order.discount_value}  €
                                                                </span>
                                                            ) : (
                                                                <span className="table-secondary-text" style={{color: "#de7878"}}>
                                                                    &nbsp;&nbsp;Скидка {order.discount_value}  €
                                                                </span>
                                                            )
                                                        )}
                                                    </span>
                                                    <span className="table-secondary-text">
                                                        Оплачено: {order.paid_amount} € • Остаток:{" "} {remaining.toFixed(2)} €
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="inline-actions">
                                                    {needDelivery && (
                                                        <span
                                                            className={`order-delivery-badge ${
                                                                order.is_printed
                                                                    ? "order-delivery-badge-success"
                                                                    : "order-delivery-badge-danger"
                                                            }`}
                                                            role="button"
                                                            tabIndex={0}
                                                            title="Скачать адресную наклейку (Word, .docx) — адрес из заказа"
                                                            onClick={() => handleDownloadDeliveryDocx(order)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    handleDownloadDeliveryDocx(order);
                                                                }
                                                            }}
                                                            style={{
                                                                cursor: deliveryDocxOrderId ? "progress" : "pointer",
                                                                opacity: deliveryDocxOrderId === order.id ? 0.6 : 1,
                                                            }}
                                                        >
                                                            🚚&nbsp;&nbsp;&nbsp;Доставка
                                                          </span>
                                                    )}
                                                    {needDelivery && !order.is_printed ? (
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            onClick={() => setOrderToDeliver(order)}
                                                        >
                                                            <Send size={18} />
                                                        </button>
                                                    ) : null}
                                                </div>

                                            </td>

                                            <td>
                                                <div className="inline-actions">
                                                    <div style={{textTransform: "uppercase"}}>{order.payment_type?.substr(0,1)}</div>
                                                    {isDeleted ? (
                                                        <span className="status-badge status-badge-inactive">
                                Удалён
                              </span>
                                                    ) : isApiOrder && order.is_paid_by_distributor ? (
                                                        <span
                                                            className="status-badge"
                                                            style={{
                                                                background: "#f59e0b",
                                                                color: "#1f2937",
                                                                borderColor: "#f59e0b",
                                                                fontWeight: 600,
                                                            }}
                                                            title="Оплачено на стороне распространителя (у нас не оплачен)"
                                                        >
                                                            Оплачен у расп.
                                                        </span>
                                                    ) : order.is_paid ? (
                                                        <span className="order-paid-cell">
                                                            <span className="status-badge status-badge-active">
                                                                Оплачен
                                                            </span>
                                                            {order.paid_at ? (
                                                                <span
                                                                    className="table-secondary-text order-paid-at"
                                                                    title="Дата и время оплаты"
                                                                >
                                                                    {new Date(order.paid_at).toLocaleString()}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    ) : (
                                                        <>
                                                    {/* добавить оплату (скрыто для ограниченных админов) */}
                                                    {!isDeleted && !isPaymentRestrictedAdmin && (
                                                        <button
                                                        type="button"
                                                        className="icon-button"
                                                        title="Внести оплату"
                                                        onClick={() => handleOpenAddPayment(order)}
                                                        >
                                                        <CreditCard size={18} />
                                                        </button>
                                                        )}
                                                        {/* применить скидку (только для неудалённых и невозвратных неоплаченных заказов) */}
                                                        {!isDeleted && !isRefunded && (
                                                        <button
                                                        type="button"
                                                        className="icon-button"
                                                        title="Применить скидку"
                                                        onClick={() => setDiscountModalOrder(order)}
                                                        >
                                                        <Percent size={18} />
                                                        </button>
                                                        )}
                                                        <span className="status-badge status-badge-inactive">
                                Не оплачен
                              </span>

                                                        </>
                                                    )}

                                                    {isRefunded && (
                                                        <span className="status-badge status-badge-inactive">
                                Возврат
                              </span>
                                                    )}

                                                    {/* Комментарий к заказу: добавить / изменить. С комментарием — иконка подсвечена,
                                                        при наведении показывается полный текст. */}
                                                    <button
                                                        type="button"
                                                        className="icon-button"
                                                        aria-label={order.comment?.trim() ? "Изменить комментарий" : "Добавить комментарий"}
                                                        title={order.comment?.trim() ? undefined : "Добавить комментарий"}
                                                        onClick={() => openCommentEditor(order)}
                                                        onMouseEnter={(e) => showCommentTip(e, order.comment)}
                                                        onMouseLeave={hideCommentTip}
                                                        style={order.comment?.trim() ? { color: "#a5b4fc" } : undefined}
                                                    >
                                                        {order.comment?.trim() ? <MessageSquareText size={18} /> : <MessageSquare size={18} />}
                                                    </button>

                                                    {/* Отправка счёта / билетов / лифершайна.
                                                        Для заказов через API распространителя (isApiOrder) ничего не отправляем — только просмотр. */}
                                                    {!isDeleted && !isRefunded && !isApiOrder && !order.is_paid && !order.is_paid_soon && !customerInfo?.is_agent && (
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Отправить счёт клиенту"
                                                            disabled={sendingOrderId === order.id}
                                                            onClick={() => runSend(order, "invoice")}
                                                        >
                                                            <FileText size={18} />
                                                        </button>
                                                    )}

                                                    {!isDeleted && !isRefunded && !isApiOrder && order.is_paid && (
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Отправить билеты клиенту"
                                                            disabled={sendingOrderId === order.id}
                                                            onClick={() => runSend(order, "tickets")}
                                                        >
                                                            <Ticket size={18} />
                                                        </button>
                                                    )}

                                                    {!isDeleted && !isRefunded && !isApiOrder && order.is_paid_soon && !order.is_paid && (
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Отправить Lieferschein распространителю"
                                                            disabled={sendingOrderId === order.id}
                                                            onClick={() => runSend(order, "lieferschein")}
                                                        >
                                                            <Truck size={18} />
                                                        </button>
                                                    )}
                                                </div>

                                            </td>

                                            <td>
                                                <div className="inline-actions">
                                                    {/* отменить / пометить удалённым */}
                                                    {!isDeleted && (
                                                        <button
                                                            type="button"
                                                            className="icon-button icon-button-danger"
                                                            title="Отменить заказ"
                                                            onClick={() => openDeleteModal(order)}
                                                        >
                                                            <XCircle size={18} />
                                                        </button>
                                                    )}

                                                    {/* восстановить удалённый заказ (API-заказы восстанавливает только распространитель) */}
                                                    {isDeleted && !isRefunded && !isApiOrder && (
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Восстановить заказ"
                                                            onClick={() => setOrderToRestore(order)}
                                                        >
                                                            <RotateCcw size={18} />
                                                        </button>
                                                    )}

                                                    {/* вернуть средства */}
                                                    {isDeleted && !isRefunded && Number(order.paid_amount) > 0 && (
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Отметить возврат средств"
                                                            onClick={() => setOrderToRefund(order)}
                                                        >
                                                            <Undo2 size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        <div className="pagination-row">
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={page === 1}
                                onClick={() =>
                                    setPage((prev) => Math.max(1, prev - 1))
                                }
                            >
                                Назад
                            </Button>

                            <span className="pagination-text">
                Страница {page} из {totalPages} • всего {totalSize} заказов
              </span>

                            <Button
                                type="button"
                                variant="secondary"
                                disabled={page === totalPages}
                                onClick={() =>
                                    setPage((prev) => Math.min(totalPages, prev + 1))
                                }
                            >
                                Вперёд
                            </Button>
                        </div>
                    </>
                )}
            </Card>

            {/* модалка внесения оплаты */}
            {selectedOrder && (
                <AddPaymentModal
                    isOpen={isAddPaymentOpen}
                    order={selectedOrder}
                    onClose={() => setIsAddPaymentOpen(false)}
                    onSubmit={async (amount, comment) => {
                        await addOrderPaymentRequest(selectedOrder.id, {
                            amount,
                            payment_type: "rechnung",
                            comment,
                        });
                        handlePaymentAdded(amount, comment);
                        setIsAddPaymentOpen(false);
                    }}
                />
            )}

            {/* модалка применения скидки к заказу */}
            {discountModalOrder && (
                <ApplyDiscountModal
                    isOpen={!!discountModalOrder}
                    order={discountModalOrder}
                    onClose={() => setDiscountModalOrder(null)}
                    onSubmit={async (discountType, discountValue) => {
                        const updated = await applyOrderDiscountRequest(discountModalOrder.id, {
                            discount_type: discountType,
                            discount_value: discountValue,
                        });
                        setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
                        setDiscountModalOrder(null);
                    }}
                />
            )}

            {/* подтверждение удаления */}
            {orderToDelete && !getPartnerOrderInfo(orderToDelete) && (
                <ConfirmModal
                    isOpen={!!orderToDelete}
                    title="Отменить заказ?"
                    description={`Вы точно хотите отменить заказ ${orderToDelete.order_number}?`}
                    confirmLabel="Удалить"
                    cancelLabel="Отмена"
                    variant="danger"
                    onCancel={closeDeleteModal}
                    onConfirm={handleConfirmDelete}
                />
            )}

            {/* Партнёрский заказ (Artist Production / Nashe): отдельное окно с номером заказа у партнёра */}
            {orderToDelete && getPartnerOrderInfo(orderToDelete) && (() => {
                const info = getPartnerOrderInfo(orderToDelete)!;
                return (
                    <Modal
                        isOpen={!!orderToDelete}
                        title={`Удалить партнёрский заказ ${orderToDelete.order_number}?`}
                        onClose={closeDeleteModal}
                        width="md"
                    >
                        <div className="partner-delete-box">
                            <div className="partner-delete-title">
                                Это заказ партнёра {info.partnerName}
                            </div>
                            <div>Номер у партнёра: <b>{info.partnerName}, {info.reference}</b></div>
                        </div>

                        {info.paidAtPartner ? (
                            <>
                                <p className="card-text">
                                    Заказ оплачен (выкуплен) у партнёра. Через API его не отменить: сначала
                                    свяжитесь с партнёром, попросите удалить заказ {info.reference} у себя и
                                    дождитесь подтверждения (по телефону, почте и т. п.). Только после этого
                                    удаляйте заказ здесь — иначе места у нас вернутся в продажу, а у партнёра
                                    останутся проданными.
                                </p>
                                <label className="partner-delete-check">
                                    <input
                                        type="checkbox"
                                        checked={partnerDeletionConfirmed}
                                        onChange={(e) => {
                                            setPartnerDeletionConfirmed(e.target.checked);
                                            setDeleteError(null);
                                        }}
                                        disabled={isDeleting}
                                    />
                                    <span>
                                        Партнёр {info.partnerName} подтвердил, что заказ {info.reference} у него тоже удалён
                                    </span>
                                </label>
                            </>
                        ) : (
                            <p className="card-text">
                                Заказ у партнёра ещё не оплачен — это резерв. При удалении резерв у партнёра будет
                                отменён автоматически через API. Если партнёр не ответит, заказ не удалится.
                            </p>
                        )}

                        {deleteError && (
                            <div className="form-global-error" style={{ marginTop: 8 }}>
                                {deleteError}
                            </div>
                        )}

                        <div className="editor-actions">
                            <Button type="button" variant="ghost" onClick={closeDeleteModal} disabled={isDeleting}>
                                Отмена
                            </Button>
                            <Button
                                type="button"
                                variant="danger"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting || (info.paidAtPartner && !partnerDeletionConfirmed)}
                            >
                                {isDeleting ? "Удаляем…" : "Удалить"}
                            </Button>
                        </div>
                    </Modal>
                );
            })()}

            {/* восстановление удалённого заказа: проверка мест и подтверждение */}
            <RestoreOrderModal
                order={orderToRestore}
                onClose={() => setOrderToRestore(null)}
                onRestored={(updated) => {
                    setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
                    setOrderToRestore(null);
                }}
            />

            {/* подтверждение возврата */}
            {orderToRefund && (
                <ConfirmModal
                    isOpen={!!orderToRefund}
                    title="Отметить возврат средств?"
                    description={`Отметить заказ ${orderToRefund.order_number} как возвращённый?`}
                    confirmLabel="Отметить возврат"
                    cancelLabel="Отмена"
                    variant="secondary"
                    onCancel={() => setOrderToRefund(null)}
                    onConfirm={handleConfirmRefund}
                />
            )}

            {orderToDeliver && (
                <ConfirmModal
                    isOpen={!!orderToDeliver}
                    title="Отметить билет как отправленный?"
                    description={`Отметить заказ ${orderToDeliver.order_number} как отправленный?`}
                    confirmLabel="Отправить билет"
                    cancelLabel="Отмена"
                    variant="danger"
                    onCancel={() => setOrderToDeliver(null)}
                    onConfirm={handleConfirmDelivery}
                />
            )}

            {/* модалка со списком билетов заказа */}
            <OrderTicketsModal
                isOpen={!!ticketsModalOrder}
                order={ticketsModalOrder}
                onClose={() => setTicketsModalOrder(null)}
                onOrderUpdated={(updated) => {
                    setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
                    setTicketsModalOrder(updated);
                }}
            />

            {/* модалка-расшифровка скидки: скидки по билетам + годовой абонемент */}
            <Modal
                isOpen={!!discountsModalOrder}
                title={
                    discountsModalOrder
                        ? `Расшифровка скидки — заказ ${discountsModalOrder.order_number}`
                        : "Расшифровка скидки"
                }
                onClose={() => setDiscountsModalOrder(null)}
                width="md"
            >
                {discountsModalOrder && (() => {
                    const rows: Array<{
                        tourLabel: string;
                        sector: string;
                        row: string;
                        seat: string;
                        discountName: string;
                        percent: number;
                        amount: number;
                    }> = [];

                    discountsModalOrder.tours.forEach((tour) => {
                        tour.items.forEach((item) => {
                            if (!item.discount_id) return;
                            const price = Number(item.price) || 0;
                            const percent = Number(item.discount_percent) || 0;
                            const amount = (price * percent) / 100;
                            rows.push({
                                tourLabel: `${tour.tour_name_ru}${tour.city_name_ru ? ` — ${tour.city_name_ru}` : ""}`,
                                sector: item.sector || "—",
                                row: item.row || "—",
                                seat: item.seat || "—",
                                discountName: item.discount_name_ru || "—",
                                percent,
                                amount,
                            });
                        });
                    });

                    const subscriptionAmount = Number(discountsModalOrder.subscription_discount_amount) || 0;
                    const subscriptionPercent = Number(discountsModalOrder.subscription_discount_percent) || 0;
                    const totalDiscount = Number(discountsModalOrder.discount_value) || 0;
                    // Всё, что не скидка абонемента: скидки по билетам, промокод, ручная скидка.
                    const otherDiscount = Math.max(0, totalDiscount - subscriptionAmount);

                    // Итоговый блок: из чего сложилась скидка заказа.
                    const summary = (
                        <div
                            style={{
                                marginTop: rows.length > 0 ? 16 : 0,
                                padding: "12px 14px",
                                // Тёмная тема панели: свои переменные вместо светлого фона.
                                background: "var(--surface-muted)",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius-sm)",
                                color: "var(--text-soft)",
                            }}
                        >
                            {otherDiscount > 0 && (
                                <div style={{display: "flex", justifyContent: "space-between", padding: "3px 0"}}>
                                    <span>Скидки по билетам и промокод</span>
                                    <b style={{color: "var(--success)"}}>-{otherDiscount.toFixed(2)} €</b>
                                </div>
                            )}
                            {subscriptionAmount > 0 && (
                                <div style={{display: "flex", justifyContent: "space-between", padding: "3px 0"}}>
                                    <span>
                                        Годовой абонемент клуба{subscriptionPercent > 0 ? ` (${subscriptionPercent}%)` : ""}
                                    </span>
                                    <b style={{color: "#a78bfa"}}>-{subscriptionAmount.toFixed(2)} €</b>
                                </div>
                            )}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "6px 0 0",
                                    marginTop: 6,
                                    borderTop: "1px solid var(--border-strong)",
                                    fontWeight: 700,
                                    color: "var(--text)",
                                }}
                            >
                                <span>Итоговая скидка</span>
                                <span>-{totalDiscount.toFixed(2)} €</span>
                            </div>
                            {subscriptionAmount > 0 && (
                                <p className="card-text" style={{margin: "8px 0 0", fontSize: 12, color: "var(--text-muted)"}}>
                                    Скидка абонемента считается от цены билетов после всех остальных скидок.
                                </p>
                            )}
                        </div>
                    );

                    if (rows.length === 0) {
                        return (
                            <>
                                <p className="card-text">Нет билетов со скидкой по этому заказу.</p>
                                {summary}
                            </>
                        );
                    }

                    return (
                        <>
                        <div style={{overflowX: "auto"}}>
                            <table style={{width: "100%", borderCollapse: "collapse"}}>
                                <thead>
                                    <tr>
                                        <th style={{textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--border-strong)"}}>Мероприятие</th>
                                        <th style={{textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--border-strong)"}}>Место</th>
                                        <th style={{textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--border-strong)"}}>Скидка</th>
                                        <th style={{textAlign: "right", padding: "6px 8px", borderBottom: "1px solid var(--border-strong)"}}>Сумма</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, idx) => (
                                        <tr key={idx}>
                                            <td style={{padding: "6px 8px", borderBottom: "1px solid var(--border)"}}>{r.tourLabel}</td>
                                            <td style={{padding: "6px 8px", borderBottom: "1px solid var(--border)"}}>
                                                {r.sector}, ряд {r.row}, место {r.seat}
                                            </td>
                                            <td style={{padding: "6px 8px", borderBottom: "1px solid var(--border)"}}>
                                                {r.discountName} ({r.percent}%)
                                            </td>
                                            <td
                                                style={{
                                                    padding: "6px 8px",
                                                    borderBottom: "1px solid var(--border)",
                                                    textAlign: "right",
                                                    color: "var(--success)",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                -{r.amount.toFixed(2)} €
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {summary}
                        </>
                    );
                })()}
                <div className="editor-actions">
                    <Button type="button" variant="ghost" onClick={() => setDiscountsModalOrder(null)}>
                        Закрыть
                    </Button>
                </div>
            </Modal>

            {/* модалка с комментарием к заказу */}
            <Modal
                isOpen={!!commentOrder}
                title={
                    commentOrder
                        ? `${commentOrder.comment?.trim() ? "Комментарий" : "Новый комментарий"} к заказу ${commentOrder.order_number}`
                        : "Комментарий"
                }
                onClose={() => !isSavingComment && setCommentOrder(null)}
                width="md"
            >
                <Field label="Комментарий" htmlFor="order-comment-edit">
                    <Textarea
                        id="order-comment-edit"
                        rows={6}
                        value={commentDraft}
                        placeholder="Текст комментария к заказу"
                        onChange={(e) => setCommentDraft(e.target.value)}
                        autoFocus
                    />
                </Field>
                {commentError && (
                    <div className="form-global-error" style={{ marginTop: 8 }}>
                        {commentError}
                    </div>
                )}
                <div className="editor-actions">
                    <Button type="button" variant="ghost" onClick={() => setCommentOrder(null)} disabled={isSavingComment}>
                        Отмена
                    </Button>
                    <Button type="button" onClick={handleSaveComment} disabled={isSavingComment}>
                        {isSavingComment ? "Сохраняем…" : "Сохранить"}
                    </Button>
                </div>
            </Modal>

            <Modal
                isOpen={!!emailOrder}
                title={emailOrder ? `Письмо по заказу ${emailOrder.order_number}` : "Письмо"}
                onClose={() => !emailSending && setEmailOrder(null)}
                width="md"
            >
                {emailOrder && (
                    <p className="table-secondary-text" style={{ marginTop: 0 }}>
                        Письмо уйдёт на {emailOrder.customer_info?.email?.trim() || "email покупателя"} так же,
                        как остальные письма ShowMust. Ответы придут на office@showmust.de.
                    </p>
                )}
                <Field label="Тема" htmlFor="order-email-subject">
                    <Input
                        id="order-email-subject"
                        type="text"
                        value={emailSubject}
                        placeholder="Тема письма"
                        onChange={(e) => setEmailSubject(e.target.value)}
                        autoFocus
                    />
                </Field>
                <Field label="Текст" htmlFor="order-email-body">
                    <Textarea
                        id="order-email-body"
                        rows={10}
                        value={emailBody}
                        placeholder="Текст письма"
                        onChange={(e) => setEmailBody(e.target.value)}
                    />
                </Field>
                {emailError && (
                    <div className="form-global-error" style={{ marginTop: 8 }}>
                        {emailError}
                    </div>
                )}
                <div className="editor-actions">
                    <Button type="button" variant="ghost" onClick={() => setEmailOrder(null)} disabled={emailSending}>
                        Отмена
                    </Button>
                    <Button type="button" onClick={handleSendOrderEmail} disabled={emailSending}>
                        {emailSending ? "Отправляем…" : "Отправить"}
                    </Button>
                </div>
            </Modal>

            {/* Подсказка с полным комментарием при наведении на иконку */}
            {commentTip && (
                <div
                    className="order-comment-tooltip"
                    style={{
                        left: commentTip.x,
                        top: commentTip.y,
                        transform: commentTip.above ? "translate(-50%, -100%)" : "translateX(-50%)",
                    }}
                    role="tooltip"
                >
                    {commentTip.text}
                </div>
            )}
        </div>
    );
};

export default OrdersPage;
